import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Eye,
  EyeOff,
  Calendar,
  MapPin,
  Users,
  Clock,
  Award,
  Activity,
  TrendingUp,
  Globe,
  Target,
  Sparkles,
  Filter,
  Search,
  Upload,
  Image,
  Video,
  Mail,
  Settings,
  Database,
  Heart,
} from 'lucide-react';
import { db, auth, storage, JobPosition, ActivityEvent, VolunteeringSubmission } from '@/lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';

const Admin = () => {
  const [activeTab, setActiveTab] = useState('careers');
  const [jobs, setJobs] = useState<JobPosition[]>([]);
  const [activities, setActivities] = useState<ActivityEvent[]>([]);
  const [volunteering, setVolunteering] = useState<VolunteeringSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<JobPosition | ActivityEvent | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [notificationSubscribers, setNotificationSubscribers] = useState(0);
  const [isSendingNotifications, setIsSendingNotifications] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  // Form states
  const [jobForm, setJobForm] = useState({
    title: '',
    department: '',
    location: '',
    type: 'full-time' as const,
    experience: '',
    salary: '',
    description: '',
    job_description: [] as string[],
    requirements: [] as string[],
    benefits: [] as string[],
    is_active: true,
  });

  const [activityForm, setActivityForm] = useState({
    title: '',
    description: '',
    date: '',
    location: '',
    attendees: '',
    duration: '',
    category: '',
    icon_name: 'Award',
    color_scheme: 'from-blue-500 to-cyan-500',
    image_url: '',
    video_url: '',
    youtube_url: '',
    media_type: 'image' as 'image' | 'video' | 'youtube',
    carousel_order: 0,
    is_active: true,
  });

  const iconOptions = [
    { value: 'Award', icon: Award },
    { value: 'Sparkles', icon: Sparkles },
    { value: 'Users', icon: Users },
    { value: 'Activity', icon: Activity },
    { value: 'TrendingUp', icon: TrendingUp },
    { value: 'Globe', icon: Globe },
    { value: 'Target', icon: Target },
    { value: 'Calendar', icon: Calendar },
  ];

  const colorSchemes = [
    'from-blue-500 to-cyan-500',
    'from-purple-500 to-pink-500',
    'from-green-500 to-emerald-500',
    'from-orange-500 to-red-500',
    'from-teal-500 to-blue-500',
    'from-indigo-500 to-purple-500',
  ];


  useEffect(() => {
    checkAuth();
    checkStorageSetup();
    loadNotificationSubscribers();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated, activeTab]); // loadData is intentionally omitted - it's called internally

  const checkStorageSetup = async () => {
    try {
      const buckets = await storage.listBuckets();
      console.log('Available buckets:', buckets);
      
      // Check if media bucket exists
      const mediaBucket = buckets.find(bucket => bucket.name === 'media');
      if (mediaBucket) {
        console.log('Media bucket found:', mediaBucket);
      } else {
        console.warn('Media bucket not found! Uploads will fail.');
        console.log('Attempting to create media bucket...');
        try {
          const result = await storage.createMediaBucket();
          console.log('Bucket creation result:', result);
        } catch (createError) {
          console.error('Failed to create media bucket:', createError);
        }
      }
    } catch (error) {
      console.error('Storage check error:', error);
    }
  };

  const handleCreateBucket = async () => {
    try {
      const result = await storage.createMediaBucket();
      if (result.success) {
        toast({
          title: 'Úspech!',
          description: result.message,
        });
      } else {
        toast({
          title: 'Chyba',
          description: result.message,
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Manual bucket creation error:', error);
      toast({
        title: 'Chyba',
        description: 'Nepodarilo sa vytvoriť bucket',
        variant: 'destructive'
      });
    }
  };

  const handleDiagnoseStorage = async () => {
    console.log('Running storage diagnostics...');
    try {
      const result = await storage.diagnoseBucket();
      console.log('Diagnosis result:', result);
      
      if (result.bucketExists) {
        toast({
          title: 'Storage OK',
          description: `Bucket "media" nájdený. Spojenie funguje.`,
        });
      } else {
        toast({
          title: 'Storage Problém',
          description: result.error || 'Bucket "media" neexistuje alebo nie je prístupný',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Diagnostics error:', error);
      toast({
        title: 'Diagnostika zlyhala',
        description: 'Nepodarilo sa spustiť diagnostiku',
        variant: 'destructive'
      });
    }
  };

  const checkAuth = async () => {
    try {
      const session = await auth.getSession();
      setIsAuthenticated(!!session);
      console.log('Auth check result:', !!session);
    } catch (error) {
      console.error('Auth check error:', error);
      setIsAuthenticated(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Simple password authentication - only check if password is Welcome123
      if (credentials.password === 'Welcome123') {
        setIsAuthenticated(true);
      } else {
        alert('Nesprávne heslo');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Nesprávne prihlasovacie údaje');
    }
  };

  const handleLogout = async () => {
    // Simple logout - just clear local state
    setIsAuthenticated(false);
    setCredentials({ email: '', password: '' });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'careers') {
        const data = await db.jobs.getAll();
        setJobs(data);
      } else if (activeTab === 'volunteering') {
        const data = await db.volunteering.getAll();
        setVolunteering(data);
      } else {
        const data = await db.activities.getAll();
        setActivities(data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (activeTab === 'careers') {
        if (editingItem) {
          await db.jobs.update(editingItem.id, jobForm);
        } else {
          const newJob = await db.jobs.create({ ...jobForm, posted_date: new Date().toISOString() });
          
          // Send notifications for new job postings
          if (newJob) {
            const subscribers = await db.jobNotifications.getActiveSubscribers();
            if (subscribers.length > 0) {
              console.log(`Sending notifications for new job: ${newJob.title} to ${subscribers.length} subscribers`);
              
              // In a real app, you would integrate with an email service here
              // For now, we'll just mark them as notified
              for (const subscriber of subscribers) {
                await db.jobNotifications.markAsNotified(subscriber.id);
              }
              
              toast({
                title: 'Ponuka vytvorená a notifikácie odoslané!',
                description: `Notifikácie boli odoslané ${subscribers.length} odberateľom.`,
              });
            }
          }
        }
      } else {
        if (editingItem) {
          await db.activities.update(editingItem.id, activityForm);
        } else {
          await db.activities.create(activityForm);
        }
      }
      
      setShowForm(false);
      setEditingItem(null);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving:', error);
    }
  };

  const handleFileUpload = async (file: File, type: 'image' | 'video') => {
    console.log('Starting file upload:', { fileName: file.name, fileSize: file.size, fileType: file.type, type });
    setUploading(true);
    setUploadProgress(0);
    
    try {
      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 100);
      
      console.log('Calling storage.uploadFile...');
      const url = await storage.uploadFile(file, 'activities');
      console.log('Upload successful, URL:', url);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (type === 'image') {
        setActivityForm({ ...activityForm, image_url: url, media_type: 'image' });
      } else {
        setActivityForm({ ...activityForm, video_url: url, media_type: 'video' });
      }
      
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
      }, 500);
      
    } catch (error: unknown) {
      console.error('Error uploading file:', error);
      const errorMessage = error instanceof Error ? error.message : 'Neznáma chyba'
      alert(`Chyba pri nahrávaní súboru: ${errorMessage}`);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const type = file.type.startsWith('image/') ? 'image' : 'video';
      handleFileUpload(file, type);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'video') => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0], type);
    }
  };

  const removeFile = () => {
    setActivityForm({ 
      ...activityForm, 
      image_url: '', 
      video_url: '',
      youtube_url: '',
      media_type: 'image'
    });
  };

  const resetForm = () => {
    setJobForm({
      title: '',
      department: '',
      location: '',
      type: 'full-time',
      experience: '',
      salary: '',
      description: '',
      job_description: [],
      requirements: [],
      benefits: [],
      is_active: true,
    });
    setActivityForm({
      title: '',
      description: '',
      date: '',
      location: '',
      attendees: '',
      duration: '',
      category: '',
      icon_name: 'Award',
      color_scheme: 'from-blue-500 to-cyan-500',
      image_url: '',
      video_url: '',
      youtube_url: '',
      media_type: 'image' as 'image' | 'video' | 'youtube',
      carousel_order: 0,
      is_active: true,
    });
  };

  const handleEdit = (item: JobPosition | ActivityEvent) => {
    setEditingItem(item);
    if (activeTab === 'careers') {
      setJobForm({
        title: item.title || '',
        department: item.department || '',
        location: item.location || '',
        type: item.type || 'full-time',
        experience: item.experience || '',
        salary: item.salary || '',
        description: item.description || '',
        job_description: item.job_description || [],
        requirements: item.requirements || [],
        benefits: item.benefits || [],
        is_active: item.is_active ?? true,
      });
    } else {
      setActivityForm({
        title: item.title || '',
        description: item.description || '',
        date: item.date || '',
        location: item.location || '',
        attendees: item.attendees || '',
        duration: item.duration || '',
        category: item.category || '',
        icon_name: item.icon_name || 'Award',
        color_scheme: item.color_scheme || 'from-blue-500 to-cyan-500',
        image_url: item.image_url || '',
        video_url: item.video_url || '',
        youtube_url: item.youtube_url || '',
        media_type: item.media_type || 'image',
        carousel_order: item.carousel_order || 0,
        is_active: item.is_active ?? true,
      });
    }
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Ste si istý, že chcete zmazať túto položku?')) {
      try {
        if (activeTab === 'careers') {
          await db.jobs.delete(id);
        } else if (activeTab === 'volunteering') {
          await db.volunteering.delete(id);
        } else {
          await db.activities.delete(id);
        }
        loadData();
      } catch (error) {
        console.error('Error deleting:', error);
      }
    }
  };

  const loadNotificationSubscribers = async () => {
    try {
      console.log('Loading notification subscribers...');
      const subscribers = await db.jobNotifications.getActiveSubscribers();
      console.log('Found subscribers:', subscribers.length);
      setNotificationSubscribers(subscribers.length);
    } catch (error) {
      console.error('Error loading notification subscribers:', error);
    }
  };

  const handleSendJobNotifications = async () => {
    setIsSendingNotifications(true);
    try {
      const subscribers = await db.jobNotifications.getActiveSubscribers();
      
      if (subscribers.length === 0) {
        toast({
          title: 'Žiadni odberatelia',
          description: 'Nemáte žiadnych aktívnych odberateľov notifikácií.',
        });
        return;
      }

      // Simple notification simulation - in real app, you'd integrate with email service
      console.log(`Sending notifications to ${subscribers.length} subscribers...`);
      
      // Mark all as notified (in real app, you'd do this after successful email send)
      for (const subscriber of subscribers) {
        await db.jobNotifications.markAsNotified(subscriber.id);
      }

      toast({
        title: 'Notifikácie odoslané!',
        description: `Úspešne odoslané notifikácie ${subscribers.length} odberateľom.`,
      });
      
      // Reload subscriber count
      await loadNotificationSubscribers();
      
    } catch (error) {
      console.error('Error sending notifications:', error);
      toast({
        title: 'Chyba',
        description: 'Nepodarilo sa odoslať notifikácie. Skúste to prosím znova.',
        variant: 'destructive'
      });
    } finally {
      setIsSendingNotifications(false);
    }
  };

  const toggleActive = async (item: JobPosition | ActivityEvent) => {
    try {
      if (activeTab === 'careers') {
        await db.jobs.update(item.id, { is_active: !item.is_active });
      } else {
        await db.activities.update(item.id, { is_active: !item.is_active });
      }
      loadData();
    } catch (error) {
      console.error('Error toggling active:', error);
    }
  };

  const filteredJobs = jobs.filter(job => 
    job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredActivities = activities.filter(activity =>
    activity.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    activity.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    activity.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const volunteeringTypeLabel: Record<string, string> = {
    volunteering: 'Dobrovoľníctvo',
    fundraising: 'Fundraising / zbierka',
    mentoring: 'Mentoring',
    environment: 'Životné prostredie / odpad',
    community: 'Komunitná aktivita',
    other: 'Iné',
  };

  const volunteeringFreqLabel: Record<string, string> = {
    once: 'Raz',
    occasionally: 'Občas',
    regularly: 'Pravidelne',
  };

  const filteredVolunteering = volunteering.filter((item) => {
    const term = searchTerm.toLowerCase();
    return (
      item.full_name.toLowerCase().includes(term) ||
      item.email.toLowerCase().includes(term) ||
      (item.department || '').toLowerCase().includes(term) ||
      (item.organization || '').toLowerCase().includes(term) ||
      item.description.toLowerCase().includes(term)
    );
  });

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md mx-auto"
        >
          <Card className="bg-gray-800/50 backdrop-blur-sm border-gray-700/50">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold text-white">
                Admin Panel
              </CardTitle>
              <CardDescription className="text-center text-gray-400">
                Zadajte heslo pre prístup
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-6">
                <div className="space-y-2">
                  <Label className="text-white block text-center">Heslo</Label>
                  <Input
                    type="password"
                    placeholder="Zadajte heslo"
                    value={credentials.password}
                    onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white text-center"
                    required
                  />
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-blue-500 to-purple-600">
                  Prihlásiť sa
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700/50 p-4">
        <div className="container mx-auto flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Zobraziť web
            </Button>
            <Button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700"
            >
              Odhlásiť sa
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto p-4">
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-gray-800 border-gray-700">
            <TabsTrigger value="careers" className="text-gray-300 data-[state=active]:bg-gray-700 data-[state=active]:text-white">
              Pracovné ponuky
            </TabsTrigger>
            <TabsTrigger value="activities" className="text-gray-300 data-[state=active]:bg-gray-700 data-[state=active]:text-white">
              Aktivity
            </TabsTrigger>
            <TabsTrigger value="volunteering" className="text-gray-300 data-[state=active]:bg-gray-700 data-[state=active]:text-white">
              Dobrovoľníctvo
            </TabsTrigger>
          </TabsList>

          <TabsContent value="careers" className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Input
                  placeholder="Hľadať..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="max-w-xs bg-gray-800 border-gray-700 text-white"
                />
                <Button
                  onClick={() => {
                    setEditingItem(null);
                    resetForm();
                    setShowForm(true);
                  }}
                  className="bg-gradient-to-r from-blue-500 to-purple-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Pridať ponuku
                </Button>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-sm text-gray-300">
                  👥 {notificationSubscribers} odberateľov
                </div>
                <Button
                  onClick={handleSendJobNotifications}
                  disabled={isSendingNotifications || notificationSubscribers === 0}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                >
                  {isSendingNotifications ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Odosiela sa...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Odoslať notifikácie
                    </>
                  )}
                </Button>
              </div>
            </div>

            <Card className="bg-gray-800/50 backdrop-blur-sm border-gray-700/50">
              <CardHeader>
                <CardTitle className="text-white">Pracovné ponuky ({filteredJobs.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-gray-400">Načítava sa...</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-gray-300">Názov</TableHead>
                        <TableHead className="text-gray-300">Oddelenie</TableHead>
                        <TableHead className="text-gray-300">Lokalita</TableHead>
                        <TableHead className="text-gray-300">Typ</TableHead>
                        <TableHead className="text-gray-300">Plat</TableHead>
                        <TableHead className="text-gray-300">Status</TableHead>
                        <TableHead className="text-gray-300">Akcie</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredJobs.map((job) => (
                        <TableRow key={job.id}>
                          <TableCell className="text-white">{job.title}</TableCell>
                          <TableCell className="text-gray-300">{job.department}</TableCell>
                          <TableCell className="text-gray-300">{job.location}</TableCell>
                          <TableCell>
                            <Badge
                              className={
                                job.type === 'full-time'
                                  ? 'bg-green-500/20 text-green-300'
                                  : job.type === 'part-time'
                                  ? 'bg-blue-500/20 text-blue-300'
                                  : 'bg-purple-500/20 text-purple-300'
                              }
                            >
                              {job.type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-green-400 font-semibold">{job.salary}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleActive(job)}
                              className={job.is_active ? 'text-green-400' : 'text-red-400'}
                            >
                              {job.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                            </Button>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(job)}
                                className="text-blue-400 hover:text-blue-300"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(job.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="activities" className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Input
                  placeholder="Hľadať..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="max-w-xs bg-gray-800 border-gray-700 text-white"
                />
                <Button
                  onClick={() => {
                    setEditingItem(null);
                    resetForm();
                    setShowForm(true);
                  }}
                  className="bg-gradient-to-r from-blue-500 to-purple-600"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Pridať aktivitu
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleDiagnoseStorage}
                  variant="outline"
                  className="border-blue-600 text-blue-400 hover:bg-blue-900/20"
                  title="Diagnostikovať storage"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Diagnostika
                </Button>
                <Button
                  onClick={handleCreateBucket}
                  variant="outline"
                  className="border-yellow-600 text-yellow-400 hover:bg-yellow-900/20"
                  title="Vytvoriť úložný bucket (ak chýba)"
                >
                  <Database className="w-4 h-4 mr-2" />
                  Storage Bucket
                </Button>
              </div>
            </div>

            <Card className="bg-gray-800/50 backdrop-blur-sm border-gray-700/50">
              <CardHeader>
                <CardTitle className="text-white">Aktivity ({filteredActivities.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-gray-400">Načítava sa...</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-gray-300">Názov</TableHead>
                        <TableHead className="text-gray-300">Média</TableHead>
                        <TableHead className="text-gray-300">Kategória</TableHead>
                        <TableHead className="text-gray-300">Dátum</TableHead>
                        <TableHead className="text-gray-300">Lokalita</TableHead>
                        <TableHead className="text-gray-300">Status</TableHead>
                        <TableHead className="text-gray-300">Akcie</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredActivities.map((activity) => (
                        <TableRow key={activity.id}>
                          <TableCell className="text-white font-medium">{activity.title}</TableCell>
                          <TableCell className="text-gray-300">
                            {(activity.image_url || activity.video_url) ? (
                              <img 
                                src={activity.image_url || activity.video_url} 
                                alt={activity.title}
                                className="w-12 h-12 object-cover rounded-lg border border-gray-600"
                                onError={(e) => {
                                  e.currentTarget.src = '/placeholder-activity.jpg';
                                }}
                              />
                            ) : (
                              <span className="text-gray-500 text-sm">Žiadne média</span>
                            )}
                          </TableCell>
                          <TableCell className="text-gray-300">{activity.category}</TableCell>
                          <TableCell className="text-gray-300">
                            {new Date(activity.date).toLocaleDateString('sk-SK')}
                          </TableCell>
                          <TableCell className="text-gray-300">{activity.location}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleActive(activity)}
                              className={activity.is_active ? 'text-green-400' : 'text-red-400'}
                            >
                              {activity.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                            </Button>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(activity)}
                                className="text-blue-400 hover:text-blue-300"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(activity.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="volunteering" className="space-y-4">
            <div className="flex items-center justify-between">
              <Input
                placeholder="Hľadať meno, e-mail, oddelenie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-xs bg-gray-800 border-gray-700 text-white"
              />
              <div className="text-sm text-gray-300">
                {filteredVolunteering.length} nahlásení
              </div>
            </div>

            <Card className="bg-gray-800/50 backdrop-blur-sm border-gray-700/50">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Heart className="w-5 h-5 text-secondary" />
                  Social Impact ({filteredVolunteering.length})
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Nové nahlásenia chodia e-mailom na tomas.ficek01@artifex-systems.com a andreja.rebejova@artifex-systems.com
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-gray-400 py-8 text-center">Načítavam...</div>
                ) : filteredVolunteering.length === 0 ? (
                  <div className="text-gray-400 py-8 text-center">Zatiaľ nikto nič neodoslal.</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-gray-700">
                        <TableHead className="text-gray-300">Dátum</TableHead>
                        <TableHead className="text-gray-300">Meno</TableHead>
                        <TableHead className="text-gray-300">E-mail</TableHead>
                        <TableHead className="text-gray-300">Oddelenie</TableHead>
                        <TableHead className="text-gray-300">Aktivita</TableHead>
                        <TableHead className="text-gray-300">Popis</TableHead>
                        <TableHead className="text-gray-300">Champion</TableHead>
                        <TableHead className="text-gray-300"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredVolunteering.map((item) => (
                        <TableRow key={item.id} className="border-gray-700">
                          <TableCell className="text-gray-400 whitespace-nowrap">
                            {new Date(item.created_at).toLocaleString('sk-SK')}
                          </TableCell>
                          <TableCell className="text-white font-medium">{item.full_name}</TableCell>
                          <TableCell>
                            <a href={`mailto:${item.email}`} className="text-blue-400 hover:underline">
                              {item.email}
                            </a>
                          </TableCell>
                          <TableCell className="text-gray-300">{item.department || '—'}</TableCell>
                          <TableCell className="text-gray-300">
                            <div>{volunteeringTypeLabel[item.activity_type] || item.activity_type}</div>
                            <div className="text-xs text-gray-500">
                              {item.organization || volunteeringFreqLabel[item.frequency || ''] || ''}
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-300 max-w-xs">
                            <p className="line-clamp-3 whitespace-pre-wrap">{item.description}</p>
                          </TableCell>
                          <TableCell>
                            {item.interested_in_champion ? (
                              <Badge className="bg-green-500/20 text-green-300">Áno</Badge>
                            ) : (
                              <span className="text-gray-500">Nie</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(item.id)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-800 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingItem ? 'Upraviť' : 'Pridať'} {activeTab === 'careers' ? 'pracovnú ponuku' : 'aktivitu'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Vyplňte všetky potrebné informácie
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {activeTab === 'careers' ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Názov pozície</Label>
                    <Input
                      value={jobForm.title}
                      onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-white">Oddelenie</Label>
                    <Input
                      value={jobForm.department}
                      onChange={(e) => setJobForm({ ...jobForm, department: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-white">Lokalita</Label>
                    <Input
                      value={jobForm.location}
                      onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-white">Typ pracovného pomeru</Label>
                    <select
                      value={jobForm.type}
                      onChange={(e) => setJobForm({ ...jobForm, type: e.target.value as 'full-time' | 'part-time' | 'contract' })}
                      className="w-full bg-gray-700 border-gray-600 text-white rounded-md px-3 py-2"
                    >
                      <option value="full-time">Plný úväzok</option>
                      <option value="part-time">Polovičný úväzok</option>
                      <option value="contract">Zmluva</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-white">Požadovaná skúsenosť</Label>
                    <Input
                      value={jobForm.experience}
                      onChange={(e) => setJobForm({ ...jobForm, experience: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-white">Plat</Label>
                    <Input
                      value={jobForm.salary}
                      onChange={(e) => setJobForm({ ...jobForm, salary: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white"
                      placeholder="€1,800 - €2,200 mesačne"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-white">Popis</Label>
                  <Textarea
                    value={jobForm.description}
                    onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                    required
                  />
                </div>
                
                <div>
                  <Label className="text-white">Náplň práce (jedna položka na riadok)</Label>
                  <Textarea
                    value={jobForm.job_description.join('\n')}
                    onChange={(e) => setJobForm({ ...jobForm, job_description: e.target.value.split('\n').filter(r => r.trim()) })}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                    placeholder="Analýza a optimalizácia procesov...\nImplementácia nových metód..."
                  />
                </div>
                
                <div>
                  <Label className="text-white">Požiadavky (jedna na riadok)</Label>
                  <Textarea
                    value={jobForm.requirements.join('\n')}
                    onChange={(e) => setJobForm({ ...jobForm, requirements: e.target.value.split('\n').filter(r => r.trim()) })}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                    placeholder="Vysokoškolské vzdelanie...\nSkúsenosti v obore..."
                  />
                </div>
                
                <div>
                  <Label className="text-white">Benefity (jeden na riadok)</Label>
                  <Textarea
                    value={jobForm.benefits.join('\n')}
                    onChange={(e) => setJobForm({ ...jobForm, benefits: e.target.value.split('\n').filter(b => b.trim()) })}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                    placeholder="Základný plat...\nRočný bonus..."
                  />
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Názov aktivity</Label>
                    <Input
                      value={activityForm.title}
                      onChange={(e) => setActivityForm({ ...activityForm, title: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-white">Kategória</Label>
                    <Input
                      value={activityForm.category}
                      onChange={(e) => setActivityForm({ ...activityForm, category: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-white">Dátum</Label>
                    <Input
                      type="date"
                      value={activityForm.date}
                      onChange={(e) => setActivityForm({ ...activityForm, date: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-white">Lokalita</Label>
                    <Input
                      value={activityForm.location}
                      onChange={(e) => setActivityForm({ ...activityForm, location: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-white">Účastníci</Label>
                    <Input
                      value={activityForm.attendees}
                      onChange={(e) => setActivityForm({ ...activityForm, attendees: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-white">Trvanie</Label>
                    <Input
                      value={activityForm.duration}
                      onChange={(e) => setActivityForm({ ...activityForm, duration: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-white">Popis</Label>
                  <Textarea
                    value={activityForm.description}
                    onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    rows={3}
                    required
                  />
                </div>
                
                

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Typ média</Label>
                    <select
                      value={activityForm.media_type}
                      onChange={(e) => setActivityForm({ ...activityForm, media_type: e.target.value as 'image' | 'video' | 'youtube' })}
                      className="w-full bg-gray-700 border-gray-600 text-white rounded-md px-3 py-2"
                    >
                      <option value="image">Obrázok</option>
                      <option value="video">Video</option>
                      <option value="youtube">YouTube</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-white">Poradie v karuseli</Label>
                    <Input
                      type="number"
                      value={activityForm.carousel_order}
                      onChange={(e) => setActivityForm({ ...activityForm, carousel_order: parseInt(e.target.value) || 0 })}
                      className="bg-gray-700 border-gray-600 text-white"
                      min="0"
                    />
                  </div>
                </div>

                {/* File Upload Section */}
                <div className="space-y-4">
                  <Label className="text-white">Média (fotka/video)</Label>
                  
                  {uploading ? (
                    <div className="bg-gray-700 rounded-lg p-8 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                      <p className="text-white mb-2">Nahrávanie súboru...</p>
                      <div className="w-full bg-gray-600 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full transition-all duration-300" 
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  ) : activityForm.image_url || activityForm.video_url ? (
                    <div className="space-y-4">
                      <div className="relative">
                        {activityForm.media_type === 'image' ? (
                          <img 
                            src={activityForm.image_url} 
                            alt="Náhľad" 
                            className="w-full h-48 object-cover rounded-lg border border-gray-600"
                            onError={(e) => {
                              e.currentTarget.src = '/placeholder-activity.jpg';
                            }}
                          />
                        ) : (
                          <video 
                            src={activityForm.video_url} 
                            className="w-full h-48 object-cover rounded-lg border border-gray-600"
                            controls
                          />
                        )}
                        <button
                          type="button"
                          onClick={removeFile}
                          className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      onDragEnter={handleDrag}
                      onDragLeave={handleDrag}
                      onDragOver={handleDrag}
                      onDrop={handleDrop}
                      className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                        dragActive 
                          ? 'border-blue-500 bg-blue-500/10' 
                          : 'border-gray-600 hover:border-gray-500'
                      }`}
                    >
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-white mb-2">Potiahnite súbor sem alebo kliknite pre výber</p>
                      <p className="text-sm text-gray-400 mb-4">Podporované formáty: JPG, PNG, GIF pre obrázky; MP4, MOV pre videá</p>
                      
                      <div className="flex gap-4 justify-center">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleFileSelect(e, 'image')}
                          />
                          <span className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center">
                            <Image className="w-4 h-4 mr-2" />
                            Vybrať fotku
                          </span>
                        </label>
                        
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="video/*"
                            className="hidden"
                            onChange={(e) => handleFileSelect(e, 'video')}
                          />
                          <span className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center">
                            <Video className="w-4 h-4 mr-2" />
                            Vybrať video
                          </span>
                        </label>
                      </div>
                    </div>
                  )}
                  
                  {/* Manual URL Input as Fallback */}
                  <div className="space-y-2">
                    <Label className="text-white text-sm">Alebo vložte URL ručne:</Label>
                    <div className="grid grid-cols-1 gap-2">
                      {activityForm.media_type === 'image' && (
                        <Input
                          value={activityForm.image_url}
                          onChange={(e) => setActivityForm({ ...activityForm, image_url: e.target.value })}
                          className="bg-gray-700 border-gray-600 text-white"
                          placeholder="https://example.com/image.jpg"
                        />
                      )}
                      {activityForm.media_type === 'video' && (
                        <Input
                          value={activityForm.video_url}
                          onChange={(e) => setActivityForm({ ...activityForm, video_url: e.target.value })}
                          className="bg-gray-700 border-gray-600 text-white"
                          placeholder="https://example.com/video.mp4"
                        />
                      )}
                      {activityForm.media_type === 'youtube' && (
                        <Input
                          value={activityForm.youtube_url}
                          onChange={(e) => setActivityForm({ ...activityForm, youtube_url: e.target.value })}
                          className="bg-gray-700 border-gray-600 text-white"
                          placeholder="https://youtube.com/watch?v=VIDEO_ID"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
            
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                <X className="w-4 h-4 mr-2" />
                Zrušiť
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-blue-500 to-purple-600"
              >
                <Save className="w-4 h-4 mr-2" />
                Uložiť
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;